import { BueroTypeResult, ResultProps } from '../../../../../../../types/Survey/Analysis/FactorValues'
import { PrebuildType } from '../../../../../../../types/Survey/Element'

const getSum = (results: ResultProps[]): number => {
  return results.reduce((acc, n) => (acc += n.practicalQM), 0)
}

type ArbeitsplaetzeSum = {
  sum: number
}

type ArbeitsmoeglichkeitenSplit = {
  arbeitsplaetze: number
  arbeitsorte: number
}

type Sums = {
  einzelarbeit: number
  kommunikation: number
  kollaboration: number
  staufleache: number
  sonderbedarfe: number
  einzelbueros: number
  verkehrsflaechen: number
  uebergeordneteBedarfe: number
  locker: number
  drucker: number
  workcafe: number
  garderobe: number
  mitarbeiter: number
  arbeitsplaetze: ArbeitsplaetzeSum
  squareMeterPerMitarbeiter: number
  arbeitsmoeglichkeitenSplit: ArbeitsmoeglichkeitenSplit
  source: BueroTypeResult
}

const getSums = (result: BueroTypeResult): Sums => {
  const einzelarbeit = !!result.EINZELARBEIT
    ? result.EINZELARBEIT.KLASSISCHE_ARBEIT.practicalQM + result.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.practicalQM
    : 0

  const kommunikation = !!result.MEETING_GROESSE_BESPRECHUNG
    ? getSum([
        result.MEETING_GROESSE_BESPRECHUNG.PERSON_2,
        result.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4,
        result.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8,
        result.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12,
        result.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16,
      ])
    : 0

  const kollaboration = result.MEETING_GROESSE_COWORKING
    ? getSum([
        result.MEETING_GROESSE_COWORKING.PERSON_2_4,
        result.MEETING_GROESSE_COWORKING.PERSON_5_8,
        result.MEETING_GROESSE_COWORKING.PERSON_9_12,
        result.MEETING_GROESSE_COWORKING.PERSON_13_16,
      ])
    : 0

  const staufleache = !!result[PrebuildType.STAUFLAECHE] ? result[PrebuildType.STAUFLAECHE].practicalQM : 0

  const einzelbueros = !!result[PrebuildType.EINZELBUEROS] ? result[PrebuildType.EINZELBUEROS].practicalQM : 0

  const locker = !!result.locker ? result.locker.practicalQM || 0 : 0
  const drucker = !!result.drucker ? result.drucker.practicalQM || 0 : 0
  const workcafe = !!result.workcafe ? result.workcafe.practicalQM || 0 : 0
  const garderobe = !!result.garderobe ? result.garderobe.practicalQM || 0 : 0

  const sonderbedarfe = !!result[PrebuildType.SONDERBEDARFE]
    ? getSum(
        result[PrebuildType.SONDERBEDARFE].map(
          (s) =>
            ({
              practicalQM: s.practicalQM,
            } as ResultProps),
        ),
      ) +
      locker +
      drucker +
      workcafe +
      garderobe
    : locker + drucker + workcafe + garderobe

  const uebergeordneteBedarfe = !!result[PrebuildType.UEBERGEORDNETE_BEDARFE]
    ? getSum(
        result[PrebuildType.UEBERGEORDNETE_BEDARFE].map(
          (s) =>
            ({
              practicalQM: isNaN(s.practicalQM) ? 0 : s.practicalQM,
            } as ResultProps),
        ),
      )
    : 0

  const verkehrsflaechen = !!result.verkehrsflaechen ? result.verkehrsflaechen.practicalQM : 0

  const mitarbeiter = result.mitarbeiter.amount

  const arbeitsplaetzeEinzelarbeit: ArbeitsplaetzeSum = {
    sum:
      result.EINZELARBEIT.KLASSISCHE_ARBEIT.arbeitsplatzAmount +
      result.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.arbeitsplatzAmount +
      result.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.arbeitsplatzAmount,
  }

  const arbeitsplaetzeBesprechung: ArbeitsplaetzeSum = {
    sum:
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_2.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.arbeitsplatzAmount,
  }

  const arbeitsplaetzeCoworking: ArbeitsplaetzeSum = {
    sum:
      result.MEETING_GROESSE_COWORKING.PERSON_2_4.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_5_8.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_9_12.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_13_16.arbeitsplatzAmount,
  }

  const arbeitsplaetzeHybrid: ArbeitsplaetzeSum = {
    sum:
      result.HYBRID.XS.arbeitsplatzAmount +
      result.HYBRID.S.arbeitsplatzAmount +
      result.HYBRID.M.arbeitsplatzAmount +
      result.HYBRID.L.arbeitsplatzAmount +
      result.HYBRID.XL.arbeitsplatzAmount,
  }

  const arbeitsplaetze = {
    sum: arbeitsplaetzeEinzelarbeit.sum + arbeitsplaetzeBesprechung.sum + arbeitsplaetzeCoworking.sum + arbeitsplaetzeHybrid.sum,
  }

  const arbeitsmoeglichkeitenSplit = {
    arbeitsplaetze:
      result.EINZELARBEIT.KLASSISCHE_ARBEIT.arbeitsplatzAmount + result.EINZELARBEIT.FOKUSSIERTE_ARBEIT_MORE_THEN_HOUR.arbeitsplatzAmount,
    arbeitsorte:
      result.EINZELARBEIT.FOKUSSIERTE_ARBEIT_LESS_THEN_HOUR.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_2.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_3_4.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_5_8.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_9_12.arbeitsplatzAmount +
      result.MEETING_GROESSE_BESPRECHUNG.PERSON_13_16.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_2_4.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_5_8.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_9_12.arbeitsplatzAmount +
      result.MEETING_GROESSE_COWORKING.PERSON_13_16.arbeitsplatzAmount +
      result.HYBRID.XS.arbeitsplatzAmount +
      result.HYBRID.S.arbeitsplatzAmount +
      result.HYBRID.M.arbeitsplatzAmount +
      result.HYBRID.L.arbeitsplatzAmount +
      result.HYBRID.XL.arbeitsplatzAmount,
  }

  // const squareMeterPerMitarbeiter = (result.total - uebergeordneteBedarfe - sonderbedarfe) / mitarbeiter
  const squareMeterPerMitarbeiter = result.total / mitarbeiter

  return {
    einzelarbeit,
    kommunikation,
    kollaboration,
    staufleache,
    sonderbedarfe,
    einzelbueros,
    verkehrsflaechen,
    uebergeordneteBedarfe,
    locker,
    drucker,
    workcafe,
    garderobe,
    mitarbeiter,
    arbeitsplaetze,
    squareMeterPerMitarbeiter,
    arbeitsmoeglichkeitenSplit,
    source: result,
  }
}

export default getSums
